/**
 * SharedCalendar
 *
 * A single reusable calendar widget used on all three dashboards (admin, coach,
 * parent). It fetches the unified, role-scoped /calendar feed for the visible
 * range and renders month / week / day views with view switching and prev/next
 * navigation. Items are color-coded by type (session vs event vs cancellation),
 * and clicking an item opens a details modal.
 *
 * Role scoping is handled entirely by the backend based on the auth token, so
 * the same component works for every role with no props required.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import calendarService from "../../api/services/calendar.service";

const localizer = momentLocalizer(moment);

const TYPE_STYLES = {
  session: { bg: "#173963", border: "#173963", color: "#ffffff", label: "Session" },
  event: { bg: "#F3BC48", border: "#F3BC48", color: "#1a1a1a", label: "Event" },
  cancellation: { bg: "#FCA5A5", border: "#EF4444", color: "#7F1D1D", label: "Cancelled" },
};

// Build a "wide" date range that fully covers the visible grid for a given
// view/date. Month view shows leading/trailing days from adjacent months, so we
// pad to whole weeks; week/day map directly.
function rangeForView(date, view) {
  const m = moment(date);
  if (view === "day") {
    return { start: m.clone().startOf("day"), end: m.clone().endOf("day") };
  }
  if (view === "week") {
    return { start: m.clone().startOf("week"), end: m.clone().endOf("week") };
  }
  // month (default) — pad to whole weeks
  return {
    start: m.clone().startOf("month").startOf("week"),
    end: m.clone().endOf("month").endOf("week"),
  };
}

// Combine an ISO date string ("2026-09-07") with an optional "HH:MM" time into a
// JS Date. Falls back to midnight / +1h when times are missing.
function toDateTime(isoDate, hhmm, fallbackHour) {
  const base = moment(isoDate, "YYYY-MM-DD");
  if (hhmm && /^\d{1,2}:\d{2}/.test(hhmm)) {
    const [h, min] = hhmm.split(":");
    base.hour(Number(h)).minute(Number(min));
  } else {
    base.hour(fallbackHour).minute(0);
  }
  return base.toDate();
}

export default function SharedCalendar({ className = "", height = 600 }) {
  const today = useMemo(() => new Date(), []);
  const [currentDate, setCurrentDate] = useState(today);
  const [view, setView] = useState("month");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  // Day dialog: all of one day's items (opened by clicking a day cell,
  // its date number, or the "+N more" link)
  const [dayDetails, setDayDetails] = useState(null); // {date, items}

  const openDay = useCallback(
    (date) => {
      const key = moment(date).format("YYYY-MM-DD");
      const dayItems = items
        .filter((i) => i.date === key)
        .sort((a, b) => (a.start_time || "").localeCompare(b.start_time || ""));
      setDayDetails({ date: key, items: dayItems });
    },
    [items],
  );

  const fetchRange = useCallback(async () => {
    const { start, end } = rangeForView(currentDate, view);
    setLoading(true);
    setError(null);
    try {
      const data = await calendarService.getRange(
        start.format("YYYY-MM-DD"),
        end.format("YYYY-MM-DD")
      );
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError("Failed to load calendar.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [currentDate, view]);

  useEffect(() => {
    fetchRange();
  }, [fetchRange]);

  const events = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        title: item.title,
        start: toDateTime(item.date, item.start_time, 0),
        end: toDateTime(item.date, item.end_time, 1),
        allDay: !item.start_time,
        resource: item,
      })),
    [items]
  );

  const eventPropGetter = useCallback((event) => {
    const style = TYPE_STYLES[event.resource?.type] || TYPE_STYLES.session;
    return {
      style: {
        backgroundColor: style.bg,
        borderColor: style.border,
        color: style.color,
        borderRadius: "6px",
        textDecoration:
          event.resource?.type === "cancellation" ? "line-through" : "none",
      },
    };
  }, []);

  return (
    <div className={`w-full ${className}`}>
      {/* Legend */}
      <div className="flex flex-wrap items-center gap-3 mb-3 text-xs font-manrope">
        {Object.entries(TYPE_STYLES).map(([type, s]) => (
          <span key={type} className="inline-flex items-center gap-1.5">
            <span
              className="inline-block w-3 h-3 rounded-sm"
              style={{ backgroundColor: s.bg, border: `1px solid ${s.border}` }}
            />
            {s.label}
          </span>
        ))}
        {loading && <span className="text-gray-400">Loading…</span>}
        {error && <span className="text-red-500">{error}</span>}
      </div>

      <div className={loading ? "opacity-60" : ""}>
        <Calendar
          localizer={localizer}
          events={events}
          date={currentDate}
          view={view}
          views={["month", "week", "day"]}
          onView={(next) => setView(next)}
          onNavigate={(next) => setCurrentDate(next)}
          onSelectEvent={(event) => setSelected(event.resource)}
          eventPropGetter={eventPropGetter}
          selectable
          onSelectSlot={(slot) => view === "month" && openDay(slot.start)}
          onShowMore={(evts, date) => openDay(date)}
          onDrillDown={(date) => openDay(date)}
          style={{ height, minHeight: 400 }}
        />
      </div>

      {/* Day dialog: everything happening on the clicked day */}
      {dayDetails && !selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDayDetails(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 font-manrope max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#173963]">
                {moment(dayDetails.date).format("dddd, MMM D, YYYY")}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                onClick={() => setDayDetails(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            {dayDetails.items.length === 0 ? (
              <p className="text-sm text-gray-500">Nothing scheduled this day.</p>
            ) : (
              <div className="space-y-2 overflow-y-auto pr-1">
                {dayDetails.items.map((item) => {
                  const style = TYPE_STYLES[item.type] || TYPE_STYLES.session;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelected(item)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border border-gray-100 hover:border-[#F3BC48] text-left transition-colors"
                    >
                      <span
                        className="inline-block w-3 h-3 rounded-sm shrink-0"
                        style={{
                          backgroundColor: style.bg,
                          border: `1px solid ${style.border}`,
                        }}
                      />
                      <span className="flex-1 min-w-0">
                        <span
                          className={`block text-sm font-semibold text-[#173963] truncate ${
                            item.type === "cancellation" ? "line-through" : ""
                          }`}
                        >
                          {item.title}
                        </span>
                        <span className="block text-xs text-gray-500 truncate">
                          {style.label}
                          {item.extra?.location ? ` · ${item.extra.location}` : ""}
                        </span>
                      </span>
                      <span className="text-xs text-gray-500 shrink-0">
                        {item.start_time
                          ? `${item.start_time}${item.end_time ? ` – ${item.end_time}` : ""}`
                          : "All day"}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-3">
              Tap an item for full details.
            </p>
          </div>
        </div>
      )}

      {/* Details modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 font-manrope"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-2">
                <span
                  className="inline-block w-3 h-3 rounded-sm"
                  style={{
                    backgroundColor: (TYPE_STYLES[selected.type] || TYPE_STYLES.session).bg,
                  }}
                />
                <span className="text-xs uppercase tracking-wide text-gray-500">
                  {(TYPE_STYLES[selected.type] || TYPE_STYLES.session).label}
                </span>
              </div>
              <button
                className="text-gray-400 hover:text-gray-600 text-xl leading-none"
                onClick={() => setSelected(null)}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <h3 className="text-lg font-semibold text-[#173963] mb-2">
              {selected.title}
            </h3>

            <div className="space-y-1.5 text-sm text-gray-700">
              <p>
                <span className="font-medium">Date: </span>
                {moment(selected.date).format("dddd, MMM D, YYYY")}
              </p>
              {(selected.start_time || selected.end_time) && (
                <p>
                  <span className="font-medium">Time: </span>
                  {selected.start_time || "—"}
                  {selected.end_time ? ` - ${selected.end_time}` : ""}
                </p>
              )}
              {selected.class_name && (
                <p>
                  <span className="font-medium">Class: </span>
                  {selected.class_name}
                </p>
              )}
              {selected.extra?.event_type && (
                <p>
                  <span className="font-medium">Type: </span>
                  {selected.extra.event_type}
                </p>
              )}
              {selected.extra?.location && (
                <p>
                  <span className="font-medium">Location: </span>
                  {selected.extra.location}
                </p>
              )}
              {selected.extra?.reason && (
                <p>
                  <span className="font-medium">Reason: </span>
                  {selected.extra.reason}
                </p>
              )}
              {selected.extra?.description && (
                <p className="text-gray-600">{selected.extra.description}</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
